<!--
Map builder alias admin screen.
@author crm4f
-->

<template>
  
  <div class="aliasAdminContainer">
    <div class="aliasOptionsContainer">

      <div class="btn-group" role="group" aria-label="newItemOptions">
        <button type="button" :disabled="currentAlias == null || currentAlias?.aliasName" class="btn btn-primary active btn-sm" @click="saveCurrentAlias()">Save</button>
        <button type="button" :disabled="currentAlias == null" class="btn btn-primary active btn-sm" @click="clearCurrentAlias()">Cancel</button>
        <button type="button" :disabled="currentAlias == null" class="btn btn-primary active btn-sm" @click="deleteCurrentAlias()">Delete</button>
      </div>

      <!-- NEW ALIAS -->
      <div class="optionRow">
        <button 
          style="width:auto !important" 
          class="btn btn-primary active btn-sm" 
          @click="createNewAlias()">
          New Alias
        </button>

        <select 
          @change="aliasChanged()"
          class="form-control-sm" 
          v-model="currentAlias">
          <option v-for="(alias,i) in aliases" :value="alias" :key="i">{{ alias.aliasText }}</option>
        </select>
        <br>
      </div>

      <div v-if="currentAlias !== null">

        <!-- FEATURE ID -->
        <div class="optionRow">
          <div>
            <label for="id">Id:</label>
          </div>
          <input 
            id="id"
            :disabled="true" 
            :class="{'inputDisabled' :currentAlias == null}" 
            class="form-control-sm" 
            v-model="currentAlias.aliasId"
            label="id"
          />
          <br>
        </div>

        <!-- FEATURE NAME -->
        <div class="optionRow">
          <div>
            <label for="name">Name:</label>
          </div>
          <input 
            label="name"
            id="name"
            :disabled="currentAlias == null"
            :class="{'inputDisabled' :currentAlias == null}"
            class="form-control-sm"
            v-model="currentAlias.aliasText"
            required
          />
          <br>
        </div>

        <!-- BUILDING -->
        <div class="optionRow">
          <div>
            <label for="building">Building:</label>
          </div>
          <select  
            @change="getRooms()"
            v-model="currentBuilding"
            label="building"
            id="building"
            :disabled="currentAlias == null" 
            :class="{'inputDisabled' :currentAlias == null}" 
            class="form-control-sm" >
            <option v-for="(building, i) in buildings" 
              :value="building"
              :key="i"
              :selected='currentBuilding?.locationId != null && building.buildingId == currentBuilding.locationId'>
              {{ building.buildingName  }}
            </option>
          </select>
          <br>
        </div>

        <!-- ROOM -->
        <div class="optionRow">
          <div>
            <label for="dirs">Room:</label>
          </div>
          <select  
            v-model="currentRoom"
            label="building"
            id="building"
            :disabled="currentBuilding == null || rooms == null" 
            :class="{'inputDisabled' :currentBuilding == null}" 
            class="form-control-sm" >
            <option v-for="(room ,i) in rooms" 
              :value="room"
              :key="i"
              :selected='currentRoom?.locationId == room.roomId'>
              {{ room.roomName }}
            </option>
          </select>
          <br>
        </div>

      </div>

      <div style="color: #003865; margin-top: 25%" v-else>
        <h1 class="display-5">Select or create an alias to begin editing...</h1>
        <font-awesome-icon style="font-size: 50px;" icon="fa-solid fa-list" />
      </div>

    </div>

    <div class="gmapContainer">
      <GMapMap
        :center='DEFAULT_CAMPUS_LATLNG'
        :zoom='17'
        class='mapCanvas tallMap'
        id="campusMap"
        ref='mapRef'
        style="width: 100%; height: 100vh"
        :options='{
          styles: getStyles(),
          fullscreenControl: false,
          mapTypeControl: false,
          mapTypeControlOptions: {
            position: google && google.maps.ControlPosition.TOP_LEFT,
            style: google && google.maps.MapTypeControlStyle.DROPDOWN_MENU
          },
          streetViewControl: true,
          zoomControl: true
        }' 
      >
        <GMapMarker v-if="currentBuilding !== null || currentRoom !== null"
          ref="aliasMarker"
          :key="index"
          :position="determinePos(currentBuilding != null ? currentBuilding : currentRoom)"
          :clickable="true"
          :draggable="false"
          @click="openInfoWindow(currentAlias.aliasId)"
          >
            <GMapInfoWindow @closeclick="openInfoWindow(null)" :closeclick="true" :opened="currentAlias.aliasId == openedInfoWindow">
              <div>
                {{ currentAlias.aliasText }}
              </div>
            </GMapInfoWindow>
        </GMapMarker>

      </GMapMap>
    </div>

  </div>
</template>
  
<script setup>
import { onMounted, ref, toRaw } from 'vue'
import axios from 'axios'

const currentAlias = ref(null)
const currentBuilding = ref(null)
const currentRoom = ref(null)
const aliases = ref(null)
const buildings = ref(null)
const rooms = ref(null)
const openedInfoWindow = ref(null)
const DEFAULT_CAMPUS_LATLNG = {lat: 55.872336, lng: -4.288118}

const mapRef = ref(null)
const map = ref(null)
const google = ref(null)

onMounted(() => {
  getAliases()
  getBuildings()

  mapRef.value.$gmapApiPromiseLazy().then((gmapApi) => {
    console.log('gmapApi promise fulfilled')
    google.value = gmapApi
  }).then(() => {
    mapRef.value.$mapPromise.then((mapResponse) => {
    // Wait for Google Maps to load
      map.value = mapResponse
    })
  })
})


/**
 * Method to evaluate what latLng value to supply the Gmap.
*/
function determinePos (location) {
  const loc = toRaw(location)
  if (loc?.entrance?.latLng != null) {
    map.value.panTo({lat: Number(loc.entrance.latLng.lat), lng: Number(loc.entrance.latLng.lng)})
    return {lat: Number(loc.entrance.latLng.lat), lng: Number(loc.entrance.latLng.lng)}
  }
  else if (loc?.entrance?.lat != null) {
    map.value.panTo({lat: Number(loc.entrance.lat), lng: Number(loc.entrance.lng)})
    return {lat: Number(loc.entrance.lat), lng: Number(loc.entrance.lng)}
  } else if (loc?.latLng != null) {
    map.value.panTo({lat: Number(loc.latLng.lat), lng: Number(loc.latLng.lng)})
    return {lat: Number(loc.latLng.lat), lng: Number(loc.latLng.lng)}
  } else {
    map.value.panTo(DEFAULT_CAMPUS_LATLNG)
    return DEFAULT_CAMPUS_LATLNG
  }
}

function getBuildings() {
  axios.get('map/admin/buildings',{withCredentials:false})
  .then(response => {
    buildings.value = response.data
  })
}

function getRooms() {
  axios.get('map/admin/buildings/' + currentBuilding.value.buildingId + '/rooms',{withCredentials:false})
  .then(response => {
    rooms.value = response.data
  })
}

function getAliases () {
  axios.get('map/admin/aliases')
  .then(response => {
    aliases.value = response.data
  })
}

/**
  * Method to open/track info windows for the building markers.
*/
function openInfoWindow (id) { 
  openedInfoWindow.value = id
}

function aliasChanged () {
  console.log('alias changed')

  if (currentAlias.value != null && !currentAlias.value?.newish) {
    axios.get('map/locations/' + currentAlias.value.locId.substring(0, 3), { withCredentials: false })
    .then((response) => {
      currentBuilding.value = buildings.value.find((building) => building.buildingId == response.data.locationId)
      
      axios.get('map/admin/buildings/' + currentBuilding.value.buildingId + '/rooms',{withCredentials:false})
      .then(response => {
        rooms.value = response.data
        if (currentAlias.value.locId.length > 3) {
        axios.get('map/locations/' + currentAlias.value.locId, { withCredentials: false })
        .then((response) => {
          console.log(response.data)
          console.log(rooms.value.find((room) => room.roomId == response.data.locationId))
          currentRoom.value = rooms.value.find((room) => room.roomId == response.data.locationId)
        })
      }
      })
      map.value.panTo(determinePos(currentBuilding.value))
    })

  }
}

function createNewAlias () {
  axios.post("map/admin/aliases")
    .then((response) => {
      currentAlias.value = response.data
      currentAlias.value.newish = true
      if (currentAlias.value != null) {
        aliases.value.push(currentAlias.value) 
      }        
      aliasChanged()
    })
}

function saveCurrentAlias() {
  if (currentRoom.value != null) {
    currentAlias.value.locId = currentRoom.value.roomId
  } else {
    currentAlias.value.locId = currentBuilding.value.buildingId
  }
  axios.put('map/admin/aliases', currentAlias.value)
    .then(response => {
      if (response.status == 200) {
        alert('Update successful.')
      } else if (response.status == 401 || response.status == 403) {
        alert('You are not authenticated. Check console for details.')
      } else {
        alert('Something unexpected happened. Check console for details')
      }
      if(aliases.value != null) {
        var index = aliases.value.indexOf(currentAlias.value)
        aliases.value[index] = response.data
      }
      currentAlias.value = response.data
      currentAlias.value.newish = false
    })
}

function clearCurrentAlias () {
  if (confirm('Cancel current feature edit?')) {
    currentAlias.value = null
    currentBuilding.value = null
    currentRoom.value = null
    aliasChanged()
    getAliases()
  }
}

function deleteCurrentAlias () {
  if(confirm("Are you sure?")) {
    axios.delete("map/admin/aliases/" +currentAlias.value.aliasId)
      .then((response) => {
        if (response.status == 200) {
          alert('Update successful.')
        } else if (response.status == 401 || response.status == 403) {
          alert('You are not authenticated. Check console for details.')
        } else {
          alert('Something unexpected happened. Check console for details')
        }
        currentAlias.value = null
        currentBuilding.value = null
        currentRoom.value = null
        aliasChanged()
        getAliases()
      })    
  }      
}


/**
  * Styles config for the Gmap plugin. 
*/
function getStyles () {
  return [
    { featureType: 'road', elementType: 'geometry', stylers: [ { lightness: 100 }, { visibility: 'simplified' } ] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [ { color: '#575656' } ] },
    { featureType: 'road', elementType: 'labels.text.stroke', stylers: [ { color: '#FFFFFF' } ] },
    { featureType: 'poi', elementType: 'labels', stylers: [ { visibility: 'off' } ] } 
  ]
}
</script>

<style scoped>
.aliasAdminContainer {
  display: flex;
}

.aliasOptionsContainer {
  padding: 25px 0px 10px 10px;
  width: 60%;
}

.gmapContainer {
  width: 75%;
  padding: 20px;
  height: fit-content;
}
.mapViewContainer {
  display: flex;
}

.mapControls {
  width: 25%;
  padding: 25px 10px 10px 10px;
}

.listHeader {
  color: #003865;
  font-weight: bold;
}

.hrhalfheight {
  width: 75%;
  margin: auto;
}

.feature-list {
  margin-bottom: 15px;
}

.showFeatures, .showFeatures button {
  width: 100%;
}

.featuresContainer {
  margin-top: 15px;
}

.optionRow {
  display: flex;
  margin: 10px 0px 0px 5px;
  align-items: center;
  justify-content: space-between;
}

.optionRow > :nth-child(1) {
  margin-right: 15px;
  width: 25%;
  text-align: left;
  word-break: break-word;
  font-size: 13px;
  font-weight: bold;
}

.optionRow > :nth-child(2) {
  flex: 1;
  width: 100% !important;
}

</style>